<template>
  <Layout
    :title="meta.title"
    class="consult"
    max-width="100%"
  >
    <div class="consult--header">
      <div class="is-card">
        <div class="intro">
          <p>
            Preencha os dados para consultar as informações do seu cliente e prosseguir com a
            simulação
          </p>

          <!-- mass consult :: desktop -->
          <template v-if="!isMobile && canRequestAgenda">
            <button
              class="button ml-auto"
              @click="checkAgendaData"
            >
              <span class="icon">
                <i class="far fa-calendar-minus" />
              </span>
              <span>
                Agendar consulta em massa
              </span>
            </button>
          </template>
        </div>

        <!-- form -->
        <v-validate-observer
          ref="form"
          v-slot="{ handleSubmit, reset, failed }"
        >
          <form
            ref="form"
            class="form"
            @submit.prevent="handleSubmit(consult)"
            @reset.prevent="reset"
          >
            <!-- document -->
            <v-validate
              v-slot="{ errors }"
              rules="required|length:14|CPF"
              name="CPF"
              class="form__document"
            >
              <base-input
                v-model="form.document"
                :error-message="errors[0]"
                :show-copy-icon="true"
                type="tel"
                maxlength="15"
                mask="cpf"
                tabindex="1"
                @change="clearForm"
              >
                CPF
              </base-input>
            </v-validate>

            <!-- supplier_id -->
            <v-validate
              v-slot="{ errors }"
              rules="required"
              name="Banco"
              class="form__supplier"
            >
              <base-select
                v-if="isMobile"
                v-model="form.supplier_id"
                :error-message="errors[0]"
                :options="suppliers"
                :disabled="!suppliers.length"
                :can-filter="false"
                :featured="true"
                placeholder="Selecionar"
                tabindex="2"
              >
                Banco
              </base-select>
              <BaseDualSwitch
                v-else
                v-model="form.supplier_id"
                :error-message="errors[0]"
                :options="suppliers"
              >
                Banco
              </BaseDualSwitch>
            </v-validate>

            <!-- rates -->
            <v-validate
              v-slot="{ errors }"
              rules="required"
              name="Tabela"
              class="form__rates"
            >
              <base-select
                v-model="form.rates"
                :error-message="errors[0]"
                :options="rates"
                :disabled="!rates.length"
                :focus-on-load="!isBootstrapping"
                :searching="loadingRates"
                can-filter
                placeholder="Selecionar"
                tabindex="3"
              >
                Tabela
              </base-select>
            </v-validate>

            <button
              :class="{ recuo: failed }"
              class="button is-success"
              type="submit"
            >
              Consultar saldo
            </button>
          </form>
        </v-validate-observer>

        <!-- mass consult :: mobile -->
        <template v-if="isMobile && canRequestAgenda">
          <div class="intro">
            <button
              class="button is-fullwidth mt-5"
              @click="checkAgendaData"
            >
              <span class="icon">
                <i class="far fa-calendar-minus" />
              </span>
              <span>
                Agendar consulta em massa
              </span>
            </button>
          </div>
        </template>
      </div>
    </div>

    <div class="consult--content">
      <div class="is-card">
        <div class="title">
          <h2 class="font-xl is-bold">
            Histórico de consultas
          </h2>

          <div class="buttons icons">
            <button
              class="button"
              @click="downloadCSV"
            >
              <span class="icon download-icon">
                <DownloadSVG />
              </span>
            </button>
            <button
              class="button"
              @click="getList"
            >
              <span class="icon reload-icon">
                <ReloadSVG stroke="#6b7280" />
              </span>
            </button>
          </div>
        </div>

        <p class="text">
          Acompanhe as consultas das últimas 24 horas
        </p>

        <div class="form">
          <base-input
            v-model="form.tableSearch"
            placeholder="Buscar por nome ou CPF completo"
            @keyup.enter="getList"
          />
          <button
            class="button is-success"
            type="submit"
            @click="getList"
          >
            Buscar
          </button>
        </div>

        <BalanceWaitList
          :table-data="tableData"
          :all-rates="list.rates"
          class="balance-wait-list"
          @refresh="getList"
          @filters="filters = $event"
          @remove="(selectedRows) => removeItemsFromWaitList(selectedRows)"
        />

        <ThePagination
          ref="pagination"
          :data="pagination.data"
          :storage-key="pagination.filterKey"
          :has-limit="true"
          @paginate="getList"
        />
      </div>
    </div>

    <!-- modal already agenda -->
    <ModalAlreadyAgenda
      v-if="modal.already"
      @history="goToHistory"
      @close="toggle({ key: 'already', state: false })"
      @back="toggle({ key: 'already', state: false })"
      @cancel="() => {
        toggle({ key: 'already', state: false })
        toggle({ key: 'cancel', state: true })
      }"
    />

    <!-- modal cancel agenda -->
    <ModalCancelAgenda
      v-if="modal.cancel"
      @close="toggle({ key: 'cancel', state: false })"
      @back="toggle({ key: 'cancel', state: false })"
      @cancel="deleteAgendaData"
    />
  </Layout>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { toast } from 'bulma-toast';

import config from '@/data/config';
import { getUniqueID } from '@/utils';

import {
  bmp as bmpAPI,
  bms as bmsAPI,
  cartos as cartosAPI,
  qitech as qitechAPI,
  celcoin as celcoinAPI,
  qitechV2 as qitechV2API,
  massConsult as massConsultAPI,
  products as productsAPI,
} from '@/services';

import ReloadSVG from '@/assets/icons/Reload.vue';
import DownloadSVG from '@/assets/icons/Download.vue';

import seo from '@/utils/seo';
import loadingMixin from '@/mixins/loadingView.mixin';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import filterStorageMixin from '@/mixins/filterStorage.mixin';

import Layout from '@/layouts/Layout.vue';
import BaseInput from '@/components/Base/Input/index.vue';
import BaseSelect from '@/components/Base/Select/index.vue';
import BaseDualSwitch from '@/components/Base/DualSwitch/index.vue';

export default {
  name: 'FGTSConsultRedesign',
  components: {
    DownloadSVG,
    ReloadSVG,
    Layout,
    BaseInput,
    BaseSelect,
    BaseDualSwitch,

    // async
    ThePagination: () => import('@/components/ThePagination.vue'),
    BalanceWaitList: () => import('@/components/FGTS/BalanceWaitList.vue'),
    ModalAlreadyAgenda: () => import('@/components/MassConsult/ModalAlreadyAgenda.vue'),
    ModalCancelAgenda: () => import('@/components/MassConsult/ModalCancelAgenda.vue'),
  },
  mixins: [
    loadingMixin,
    roleManagerMixin,
    filterStorageMixin,
  ],
  data: () => ({
    meta: {
      title: 'Consulta de Saldo FGTS',
    },
    form: {
      document: '',
      supplier_id: '',
      rates: [],
      tableSearch: '',
      filters: {},
    },
    filters: {},
    list: {
      suppliers: [],
      rates: [],
    },
    tableData: [],
    versionOptions: [
      {
        name: 'Nova versão',
        value: 'new',
        tag: 'Novidade',
        text: `
          Conheça a nova versão do sistema,
          com novas funcionalidades e novo design.
        `,
      },
      {
        name: 'Versão anterior',
        value: 'old',
        tag: null,
        text: `
          Volte para a versão antiga do sistema,
          altere essa configuração a qualquer
          momento.
        `,
      },
    ],
    modal: {
      already: false,
      cancel: false,
    },
    cachedResponse: {},
    pagination: {
      data: {},
      filterKey: 'consult',
    },
    loadingRates: false,
    isBootstrapping: true,
    featuredSupplier: 'BMS',
  }),
  computed: {
    ...mapState({
      $_agenda: (state) => state.consult.agenda,
      user: (state) => state.user.userInfo,
      suppliersData: (state) => state.app.suppliers,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
    }),
    agendaData: {
      get() {
        return this.$_agenda;
      },
      set(payload) {
        this.setAgenda(payload);
      },
    },
    suppliers() {
      if (this.suppliersData.length === 0) return [];

      const arr = [];
      for (const { name, value } of [...this.suppliersData]) {
        const featured = name === this.featuredSupplier;
        arr.push({
          name,
          label: name,
          value,
          ...(featured ? { featured, message: 'Recomendada' } : ''),
        });
      }

      arr.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return 0;
      });

      return arr;
    },
    rates() {
      return this.list.rates.filter((rate) => rate.supplier_id === this.form.supplier_id);
    },
    canRequestAgenda() {
      return this.user.can_access_batch_balance_module;
    },
  },
  watch: {
    suppliers: {
      deep: true,
      handler() {
        if (
          !this.suppliers.length
          || (this.suppliers[0] && !this.suppliers[0]?.value)
        ) return;

        this.form.supplier_id = this.suppliers[0].value;
      },
    },
    'form.supplier_id': {
      handler(newVal, oldVal) {
        this.getRates({ isBootstrapping: !oldVal });
      },
    },
    filters: {
      handler() {
        sessionStorage.removeItem(this.pagination.filterKey);
        this.getList({ suppressError: true });
      },
    },
  },
  async mounted() {
    await this.getSuppliersList(config.PRODUCT_ID_FGTS);
    this.getList({ suppressError: true });
  },
  methods: {
    // utils
    ...mapActions({
      getSuppliersList: 'app/SET_SUPPLIERS',
      setAgenda: 'consult/SET_AGENDA',
    }),

    // handlers
    handlerError(err) {
      let message = err?.response?.data?.error?.message
        || (err?.error?.response?.data?.error?.message
        || err?.error?.data?.message
        || err?.message
        || err);

      if (err?.response?.data?.error?.errors) {
        const errors = Object.values(err?.response?.data?.error?.errors);

        message = message.concat(`<br> - ${errors.map((error) => error).join('<br> - ')}`);
      }

      toast({
        message,
        type: 'is-danger',
        position: 'top-center',
        dismissible: true,
        duration: 10000,
        animate: { in: 'fadeIn', out: 'fadeOut' },
      });
    },
    clearForm() {
      this.cachedResponse = {};

      this.form.tableSearch = '';
      this.form.filters = {};
    },
    toggle(modal) {
      this.$set(this.modal, modal.key, modal.state);
    },
    setAgenda(payload) {
      Object
        .keys(this.agendaData)
        .forEach((key) => {
          this.$delete(this.agendaData, key);
        });

      if (!payload) return;

      Object
        .keys(payload)
        .forEach((key) => {
          this.$set(this.agendaData, key, payload[key]);
        });
    },
    async downloadCSV() {
      this.loading = true;

      await qitechV2API
        .list({
          product_id: config.PRODUCT_ID_FGTS,
          output: 'csv',
        })
        .then(({ data }) => {
          const blob = new Blob([`\ufeff${data}`], { type: 'text/csv;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.setAttribute('download', 'consultas-fgts.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
        });
    },

    // nav
    goToHistory() {
      this.$router.push({ name: 'fgts-mass-consult-historic' });
    },

    // API

    // rates
    async getRates({ isBootstrapping = false } = {}) {
      this.form.rates = [];
      this.loadingRates = true;

      await productsAPI
        .getProductsRates(config.PRODUCT_ID_FGTS, {
          supplier_id: this.form.supplier_id,
        })
        .then(({ data }) => {
          this.list.rates = [];
          for (const rate of data.data) {
            this.list.rates.push({
              value: rate.id,
              ...rate,
            });
          }
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loadingRates = false;

          setTimeout(() => {
            if (isBootstrapping) this.isBootstrapping = false;
          }, 350);
        });
    },

    // first consult
    apiSupplier(client) {
      if (client === config.SUPPLIER_ID_BMP) return bmpAPI;
      if (client === config.SUPPLIER_ID_BMS) return bmsAPI;
      if (client === config.SUPPLIER_ID_CARTOS) return cartosAPI;
      if (client === config.SUPPLIER_ID_CELCOIN) return celcoinAPI;

      return qitechAPI;
    },
    async consult() {
      this.loading = true;

      await this
        .apiSupplier(this.form.supplier_id)
        .consult({
          document: this.form.document.replace(/\D/g, ''),
          rates: [this.form.rates],
          isFGTS: this.form.supplier_id === config.SUPPLIER_ID_CELCOIN,
        })
        .then(({ data }) => {
          // handle error 422 'Unprocessable Content '
          if (data?.data?.statusCode === 422) {
            this.handlerError({ message: data.data.error_message.description });
          }

          // has cache
          if (
            (data.data.simulations && data.data.rates)
            || (data.data.disbursed_issue_amount && data.data.periods)
          ) {
            this.cachedResponse = {};

            Object
              .keys(data.data)
              .forEach((key) => {
                this.$set(this.cachedResponse, key, data.data[key]);
              });

            this.$set(this.cachedResponse, 'cache', true);
            this.$set(this.cachedResponse, 'status', 'success');
            this.$set(this.cachedResponse, 'id', getUniqueID());

            this.tableData.unshift(this.cachedResponse);
          } else {
            this.getList();
          }
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
          this.clearForm();
        });
    },

    // wait-list
    async getList({
      bypassLoading = false,
      suppressError = false,
    } = {}) {
      if (!bypassLoading) this.loading = true;

      this.tableData = [];
      this.pagination.data = {};

      await qitechV2API
        .list({
          product_id: config.PRODUCT_ID_FGTS,
          ...this.filters,
          ...(this.getFiltersFromStorage({
            target: this.pagination.filterKey,
            raw: true,
          })),
          ...(this.form.tableSearch ? { search: this.form.tableSearch } : ''),
        })
        .then(({ data }) => {
          this.tableData = data.data.data;
          this.pagination.data = data.data;
        })
        .catch((err) => {
          if (!suppressError) this.handlerError(err);
        })
        .finally(() => {
          this.loading = false;
          this.clearForm();
        });
    },
    async removeItemsFromWaitList(selectedRows) {
      this.loading = true;

      await qitechAPI
        .removeItemsFromWaitList({
          ids: selectedRows.join(','),
        })
        .then(() => this.getList())
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
        });
    },

    // mass consult
    async checkAgendaData() {
      this.loading = true;

      await massConsultAPI
        .checkAgenda()
        .then(({ data }) => {
          this.setAgenda(data.data);
          this.toggle({ key: 'already', state: true });
        })
        .catch((err) => {
          if (err.response.status === 404) {
            this.$router.push({ name: 'fgts-mass-consult' });
          } else this.handlerError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async deleteAgendaData() {
      this.loading = true;

      await massConsultAPI
        .deleteAgenda(this.agendaData.id)
        .then(() => {
          this.setAgenda();

          toast({
            message: 'Agendamento cancelado com sucesso',
            type: 'is-success',
            position: 'bottom-center',
            dismissible: true,
            duration: 10000,
            animate: { in: 'fadeIn', out: 'fadeOut' },
          });
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
          this.toggle({ key: 'cancel', state: false });
        });
    },
  },
  metaInfo() {
    return seo.meta(this.meta);
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_buttonsVariables.scss';

$breakpoint: 1333px;

.intro {
  display: flex;
  align-items: flex-end;

  .button {
    border-color: $slate-200;

    .icon {
      color: $blue-600;
    }

    span:not(.icon) {
      font: {
        weight: 500;
        size: 14px;
      }
      line-height: 120%;
    }
  }
}

.recuo {
  margin-bottom: 20px;
}

.consult--header {
  h1 {
    margin-top: 36px;
    margin-bottom: 36px;
  }

  .form {
    $maxInputWidth: 380px;

    margin-top: 24px;
    display: flex;
    gap: 1rem;
    flex-direction: column;

    &__document {
      flex-grow: 1;
      max-width: #{$maxInputWidth};

      :deep(.control) {
        margin-bottom: 0;
      }
    }

    &__supplier {
      flex-grow: 1;
      max-width: #{$maxInputWidth};
    }
    &__rates {
      flex-grow: 1;
    }

    .button {
      width: 100%;
      height: 42px;
      margin-top: 16px;
      align-self: flex-end;
    }

    @media (min-width: $breakpoint) {
      flex-direction: row;

      .button {
        width: 200px;
      }
    }
  }
}

.consult--content {
  margin-top: 23px;

  .is-card {
    @media only screen and (min-width: 1024px) {
      padding: 40px 24px;
    }
  }

  .title {
    margin: 0 0 12px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (min-width: $breakpoint) {
      margin: 0 0 8px;
    }
  }

  .text {
    margin-bottom: 1.5rem;

    @media only screen and (min-width: 1024px) {
      font: {
          size: 14px;
          style: normal;
          weight: 400;
        }
        color: #666;
        line-height: 120%;
    }
  }

  .form {
    margin-bottom: 1.5rem;

    display: flex;
    gap: 16px;
    flex-direction: column;

    button {
      height: 42px;
    }

    :deep(.control) {
      flex: 1;
    }

    @media (min-width: $breakpoint) {
      flex-direction: row;
    }
  }

  .buttons {
    &.icons {
      .button {
        $size: 2rem;

        display: flex;
        align-items: center;
        justify-content: center;
        width: $size;
        height: $size;

        .download-icon {
          $size: 18px;

          width: $size;
          height: $size;
        }

        .reload-icon {
          $size: 14px;

          width: $size;
          height: $size;
        }

        svg {
          margin: 0;

          height: 100%;
          width: 100%;
        }
      }
    }
  }

  .button {
    @media only screen and (min-width: 1024px) {
        height: fit-content;
        padding: {
          top: 0;
          bottom: 0;
        }
    }
  }

  .balance-wait-list {
    margin-bottom: 1.5rem;
  }

  &::v-deep {
    .the-pagination {
      margin-top: 0;
    }
  }
}
</style>
